import React from 'react';
import { Box, Switch, Typography, LinearProgress, Button } from '@mui/material';
import { PdfFile } from '../../types';
import { usePdf } from '../../context/PdfContext';
import LayersIcon from '@mui/icons-material/Layers';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

interface FlattenToggleProps {
  file: PdfFile;
}

export const FlattenToggle: React.FC<FlattenToggleProps> = ({ file }) => {
  const { 
    toggleFlatten, 
    processWithFlatten, 
    downloadFlattenedPdf 
  } = usePdf();

  // Only show flatten option for PDF files
  if (file.fileType !== 'pdf') {
    return null;
  }

  const handleToggleChange = () => {
    toggleFlatten(file.id);
  };

  const handleFlattenClick = async () => {
    await processWithFlatten(file.id);
  };

  const handleDownloadClick = () => {
    downloadFlattenedPdf(file.id);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5, mb: 0.5 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        flexWrap: 'wrap', // Allow wrapping on small screens
        gap: 1 // Add spacing between wrapped elements
      }}>
        {/* Left side - Toggle switch */}
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '120px' }}>
          <Switch
            checked={!!file.flattenPdf}
            onChange={handleToggleChange}
            disabled={file.flattenProcessing}
            size="small"
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LayersIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="body2" noWrap>Flatten</Typography>
          </Box>
        </Box>
        
        {/* Middle - Status message */}
        <Box sx={{ flex: '1 1 auto', minWidth: '100px', maxWidth: '250px' }}>
          {file.hasLayers && !file.flattenPdf && !file.flattenProcessing && (
            <Typography variant="caption" color="primary" noWrap>
              Has layers (can be flattened)
            </Typography>
          )}
        </Box>
        
        {/* Right side - Action buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', ml: 'auto' }}>
          {file.flattenPdf && !file.flattenProcessing && !file.flattenedPdfBlob && (
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleFlattenClick}
              startIcon={<LayersIcon />}
            >
              Flatten
            </Button>
          )}
          
          {file.flattenedPdfBlob && (
            <Button 
              variant="contained" 
              color="primary" 
              size="small" 
              onClick={handleDownloadClick}
              startIcon={<FileDownloadIcon />}
            >
              Download
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Progress bar */}
      {file.flattenProcessing && (
        <Box sx={{ width: '100%', mt: 0.5 }}>
          <LinearProgress 
            variant="determinate" 
            value={file.flattenProgress || 0} 
            sx={{ height: 4, borderRadius: 2 }}
          />
          <Typography variant="caption" display="block" textAlign="right" mt={0.2} fontSize="0.7rem">
            {file.flattenProgress || 0}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FlattenToggle;
